import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { company, contact } from '../mock/mock';

const navItems = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About KTC' },
  { to: '/products', label: 'Products' },
  { to: '/global-reach', label: 'Global Reach' },
  { to: '/certificates', label: 'Certificates' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/updates', label: 'Updates' },
  { to: '/insights', label: 'Insights' },
  { to: '/catalogs', label: 'Catalogs' },
  { to: '/contact', label: 'Contact' },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      {/* Top utility bar */}
      <div className="ktc-chrome hidden md:block bg-ktc-greenDark text-ktc-ivory/90 text-xs">
        <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-between">
          <span className="tracking-wide">REAP Registered {company.reapId} · ISO · HACCP · Halal · FOB Port Qasim</span>
          <div className="flex items-center gap-5">
            <a href={`tel:${contact.phone}`} className="flex items-center gap-1.5 hover:text-ktc-goldLight transition-colors">
              <Phone size={13} /> {contact.phone}
            </a>
            <span className="text-ktc-ivory/50">|</span>
            <a href={`mailto:${contact.email}`} className="hover:text-ktc-goldLight transition-colors">{contact.email}</a>
          </div>
        </div>
      </div>

      <header
        className={`ktc-chrome sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-ktc-ivoryLight/95 backdrop-blur shadow-[0_4px_24px_-12px_rgba(15,51,32,0.35)]' : 'bg-ktc-ivoryLight'
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-6">
          <div className="flex items-center justify-between h-[84px]">
            <Link to="/" className="flex items-center group shrink-0">
              <img src={company.logo} alt="Kassam Trading Company" className="h-14 md:h-16 w-auto object-contain" />
            </Link>

            <nav className="hidden lg:flex items-center gap-0.5">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `px-2.5 py-2 text-[13.5px] font-medium rounded-md transition-colors whitespace-nowrap ${
                      isActive ? 'text-ktc-green' : 'text-ktc-charcoal/75 hover:text-ktc-green'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <Link
                to="/quote"
                className="hidden sm:inline-flex items-center gap-2 bg-ktc-green hover:bg-ktc-greenLight text-ktc-ivory text-[13.5px] font-medium px-5 py-2.5 rounded-md transition-colors"
              >
                Request a Quote
              </Link>
              <button
                aria-label="Menu"
                onClick={() => setOpen((v) => !v)}
                className="lg:hidden p-2 text-ktc-green"
              >
                {open ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ${open ? 'max-h-[520px]' : 'max-h-0'}`}>
          <nav className="px-5 pb-5 pt-1 bg-ktc-ivoryLight border-t border-ktc-cream flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `py-3 text-[15px] font-medium border-b border-ktc-cream/70 ${
                    isActive ? 'text-ktc-green' : 'text-ktc-charcoal/80'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/quote" className="mt-4 text-center bg-ktc-green text-ktc-ivory font-medium py-3 rounded-md">
              Request a Quote
            </Link>
          </nav>
        </div>
      </header>
    </>
  );
};

export default Navbar;
